import React, { createContext, CSSProperties, useRef, useState } from "react";
import classNames from "classnames";
import "./widgets.css";
import WidgetFrame from "../../shared/widgets/WidgetFrame";
import Loading from "../../shared/Loading";
import {
  getWidgetColspan,
  getWidgetContentHeight,
  getWidgetContainerResponsiveHeight,
  getWidgetContentHeightWithoutTitle,
} from "../../../components/channels/helpers";
import { FocusLabelRef } from "../api";

type Props = {
  id?: string;
  title: string;
  titleBackgroundOverride: string | null;
  className?: string;
  w: number; // number of colums this widget occupies (1-3)
  children?: React.ReactNode;
  loading?: boolean;
  contentClassName?: string;
  style?: CSSProperties;
  focusLabel?: FocusLabelRef | null;
  collapseToSingleColumn?: boolean;
  singleRowOnly?: boolean;
  hideTitle?: boolean;
  // Below sm the widget sizes to its content (e.g. the banner image's aspect
  // ratio) instead of the fixed widget height.
  naturalHeightOnMobile?: boolean;
  // Sizes to its content on every breakpoint — the vertical info bar may grow
  // taller than the fixed widget grid (tixxt/core#380).
  naturalHeight?: boolean;
  // Rendered in the VERTICAL info bar; widgets may adapt (e.g. the people
  // widget switches from its column grid to a plain list).
  vertical?: boolean;
};

export const WidgetContext = createContext<{
  w: number;
  vertical: boolean;
  containerRef: React.RefObject<HTMLDivElement> | null;
  // Slot at the end of the title bar a widget can portal its controls into;
  // null while the title bar is hidden.
  titleActionsElement?: HTMLElement | null;
}>({ w: 1, vertical: false, containerRef: null });

// The channel-specific sizing around the shared WidgetFrame: column span, the
// fixed widget grid and the focus label.
export default function WidgetContainer({
  id,
  title,
  titleBackgroundOverride,
  hideTitle = false,
  w,
  children,
  loading,
  className,
  contentClassName,
  style,
  focusLabel,
  collapseToSingleColumn,
  singleRowOnly,
  naturalHeightOnMobile,
  naturalHeight,
  vertical = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [titleActionsElement, setTitleActionsElement] =
    useState<HTMLElement | null>(null);

  if (collapseToSingleColumn && window.innerWidth < 640) w = 1;

  return (
    <WidgetContext.Provider
      value={{ w, vertical, containerRef, titleActionsElement }}
    >
      <WidgetFrame
        id={`widget-${id}`}
        containerRef={containerRef}
        title={title}
        titleBackgroundOverride={titleBackgroundOverride}
        hideTitle={hideTitle}
        titleClassName={"widget__title"}
        titleAccessory={
          focusLabel && (
            <span
              className="widget__label"
              style={{
                backgroundColor: focusLabel.color ?? undefined,
                color: focusLabel.text_color ?? undefined,
              }}
            >
              {focusLabel.name}
            </span>
          )
        }
        titleEnd={
          <div
            ref={setTitleActionsElement}
            className="ml-auto shrink-0 empty:hidden"
          />
        }
        className={classNames(
          "widget__container",
          naturalHeightOnMobile && "widget__container--natural-height",
          naturalHeight && "widget__container--natural-height-always",
          naturalHeight && hideTitle && "widget__container--no-title",
          w && getWidgetColspan(w),
          w &&
            !naturalHeight &&
            (naturalHeightOnMobile
              ? "sm:h-[24rem]"
              : getWidgetContainerResponsiveHeight(singleRowOnly ? 1 : w)),
          className,
        )}
        style={style}
        contentClassName={classNames(
          "widget__content grid",
          naturalHeight
            ? null
            : naturalHeightOnMobile
              ? "sm:h-[24rem]"
              : hideTitle
                ? getWidgetContentHeightWithoutTitle(w)
                : getWidgetContentHeight(w),
          contentClassName,
        )}
      >
        {loading ? <Loading /> : null}
        {children}
      </WidgetFrame>
    </WidgetContext.Provider>
  );
}
